/**
 * GraphemeToUtf16Converter - Utility for converting grapheme indices to UTF-16 code unit indices
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility handles the conversion between grapheme cluster boundaries (used by WASM)
 * and UTF-16 code unit indices (used by JavaScript strings). It uses Intl.Segmenter
 * when available for accurate grapheme boundary detection, with a fallback for
 * ASCII-safe 1:1 mapping.
 */

/**
 * Clamp a value between a minimum and maximum
 * 
 * @param v - Value to clamp
 * @param lo - Minimum value
 * @param hi - Maximum value
 * @returns Clamped value
 */
export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(v, hi));
}

/**
 * Build a mapping from grapheme indices to UTF-16 code unit indices
 * 
 * Uses Intl.Segmenter when available for accurate grapheme boundary detection.
 * Falls back to 1:1 mapping (ASCII-safe) if Segmenter is not available.
 * 
 * @param text - Input text to analyze
 * @returns Array of UTF-16 code unit indices corresponding to grapheme boundaries
 */
export function buildGraphemeToUtf16Map(text: string): number[] {
  try {
    // Build grapheme boundaries using Intl.Segmenter
    if ('Segmenter' in Intl) {
      // @ts-ignore - Intl.Segmenter may not be in all TypeScript versions
      const seg = new (Intl as any).Segmenter(undefined, { granularity: 'grapheme' });
      const segments = Array.from(seg.segment(text)) as any[];
      const arr: number[] = [0];
      for (const s of segments) {
        arr.push((s as any).index + (s as any).segment.length);
      }
      // Ensure terminal boundary
      if (arr[arr.length - 1] !== text.length) {
        arr.push(text.length);
      }
      return arr;
    }
  } catch (error) {
    // Fallback if Segmenter fails
    console.warn('[GraphemeToUtf16Converter] Intl.Segmenter failed, using fallback:', error);
  }
  
  // Fallback: 1 grapheme == 1 UTF-16 code unit (ASCII-safe)
  return Array.from({ length: text.length + 1 }, (_, i) => i);
}

/**
 * Convert a grapheme index to UTF-16 code unit index
 * 
 * @param graphemeIndex - Grapheme cluster index
 * @param utf16Map - Mapping array from buildGraphemeToUtf16Map
 * @param textLength - Length of the text (for bounds checking)
 * @returns UTF-16 code unit index
 */
export function graphemeToUtf16(
  graphemeIndex: number,
  utf16Map: number[],
  textLength: number
): number {
  const idx = clamp(graphemeIndex | 0, 0, utf16Map.length - 1);
  const u = utf16Map[idx] ?? idx;
  return clamp(u | 0, 0, textLength);
}

/**
 * Convert paragraph ranges from grapheme indices to UTF-16 code unit indices
 * 
 * This is a convenience function that combines the mapping and conversion steps
 * for processing multiple paragraph ranges at once.
 * 
 * @param text - Full document text
 * @param paragraphs - Array of paragraph metadata with grapheme indices
 * @returns Array of paragraphs with UTF-16 indices in grapheme fields
 */
export function convertParagraphRangesToUtf16<T extends {
  graphemeStart: number;
  graphemeEnd: number;
}>(
  text: string,
  paragraphs: T[]
): Array<Omit<T, 'graphemeStart' | 'graphemeEnd'> & { graphemeStart: number; graphemeEnd: number }> {
  const utf16Map = buildGraphemeToUtf16Map(text);
  
  return paragraphs.map(p => ({
    ...p,
    // Pass UTF-16 positions in the grapheme fields to satisfy current worker mapping
    graphemeStart: graphemeToUtf16(p.graphemeStart, utf16Map, text.length),
    graphemeEnd: graphemeToUtf16(p.graphemeEnd, utf16Map, text.length)
  }));
}

